// Mock data for V2 features - matches expected Firestore structure
// This data structure should match what the backend/API team will provide

// Helper function to generate sample event data arrays
function generateReturnsArray(length = 20, mean = 0.08, std = 0.12) {
  const returns = [];
  for (let i = 0; i < length; i++) {
    // Generate values around mean with some variance
    const value = mean + (Math.random() - 0.5) * std * 2;
    returns.push(parseFloat(value.toFixed(3)));
  }
  return returns;
}

function generateAccuracyArray(length = 20, mean = 0.70, std = 0.08) {
  const accuracy = [];
  for (let i = 0; i < length; i++) {
    // Generate values between 0.5 and 0.9, centered around mean
    let value = mean + (Math.random() - 0.5) * std * 2;
    value = Math.max(0.5, Math.min(0.9, value)); // Clamp between 0.5 and 0.9
    accuracy.push(parseFloat(value.toFixed(3)));
  }
  return accuracy;
}

// Sample playbooks with all V2 fields
export const mockPlaybooksV2 = [
  // Odikai playbook - high performance
  {
    id: "odikai_aggressive_growth",
    name: "Aggressive Growth Strategy",
    creator_type: "odikai",
    creator_name: "Odikai",
    is_public: true,
    avg_accuracy: 0.72,
    last_event_return: 0.15,
    accuracy_pct_last_event: 0.72,
    returns_by_event: generateReturnsArray(25, 0.10, 0.15),
    accuracy_by_event: generateAccuracyArray(25, 0.72, 0.08),
    longest_loss_streak: 2,
    longest_win_streak: 5,
    current_win_streak: 3,
    distributions_summary: {
      returns_mean: 0.10,
      returns_std: 0.15,
      accuracy_mean: 0.72,
      accuracy_std: 0.08
    },
    user_id: "system",
    created_at: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    last_updated: new Date().toISOString(),
    event_name: "UFC 305",
    event_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    portfolio_returns: 0.10,
    previous_year_returns: 1250.50,
    playbook_type: "standard",
    picks: [
      {
        pick_name: "Jon Jones",
        opponent_name: "Stipe Miocic",
        budget_amount: 0.15,
        american_odds: "-150",
        decimal_odds: 1.67,
        supporting_strategies: 8,
        type: "moneyline"
      }
    ]
  },
  // Odikai playbook - conservative
  {
    id: "odikai_conservative",
    name: "Conservative Growth Playbook",
    creator_type: "odikai",
    creator_name: "Odikai",
    is_public: true,
    avg_accuracy: 0.68,
    last_event_return: 0.08,
    accuracy_pct_last_event: 0.68,
    returns_by_event: generateReturnsArray(30, 0.06, 0.08),
    accuracy_by_event: generateAccuracyArray(30, 0.68, 0.06),
    longest_loss_streak: 1,
    longest_win_streak: 4,
    current_win_streak: 2,
    distributions_summary: {
      returns_mean: 0.06,
      returns_std: 0.08,
      accuracy_mean: 0.68,
      accuracy_std: 0.06
    },
    user_id: "system",
    created_at: new Date(Date.now() - 120 * 24 * 60 * 60 * 1000).toISOString(),
    last_updated: new Date().toISOString(),
    event_name: "UFC 305",
    event_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    portfolio_returns: 0.06,
    previous_year_returns: 850.25,
    playbook_type: "growth",
    picks: [
      {
        pick_name: "Alex Pereira",
        opponent_name: "Jiri Prochazka",
        budget_amount: 0.12,
        american_odds: "-200",
        decimal_odds: 1.50,
        supporting_strategies: 6,
        type: "moneyline"
      }
    ]
  },
  // User playbook - public
  {
    id: "user_playbook_1",
    name: "My Custom Strategy",
    creator_type: "user",
    creator_name: "John Doe",
    is_public: true,
    avg_accuracy: 0.65,
    last_event_return: 0.12,
    accuracy_pct_last_event: 0.65,
    returns_by_event: generateReturnsArray(15, 0.08, 0.10),
    accuracy_by_event: generateAccuracyArray(15, 0.65, 0.10),
    longest_loss_streak: 4,
    longest_win_streak: 3,
    current_win_streak: 1,
    distributions_summary: {
      returns_mean: 0.08,
      returns_std: 0.10,
      accuracy_mean: 0.65,
      accuracy_std: 0.10
    },
    user_id: "user123",
    created_at: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
    last_updated: new Date().toISOString(),
    event_name: "UFC 305",
    event_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    portfolio_returns: 0.08,
    previous_year_returns: 450.75,
    playbook_type: "simple",
    picks: [
      {
        pick_name: "Islam Makhachev",
        opponent_name: "Arman Tsarukyan",
        budget_amount: 0.10,
        american_odds: "-120",
        decimal_odds: 1.83,
        supporting_strategies: 5,
        type: "moneyline"
      }
    ]
  },
  // User playbook - private (shouldn't appear in Top Playbooks)
  {
    id: "user_playbook_2",
    name: "Private Strategy",
    creator_type: "user",
    creator_name: "Jane Smith",
    is_public: false,
    avg_accuracy: 0.70,
    last_event_return: 0.18,
    accuracy_pct_last_event: 0.70,
    returns_by_event: generateReturnsArray(10, 0.12, 0.14),
    accuracy_by_event: generateAccuracyArray(10, 0.70, 0.09),
    longest_loss_streak: 2,
    longest_win_streak: 2,
    current_win_streak: 0,
    distributions_summary: {
      returns_mean: 0.12,
      returns_std: 0.14,
      accuracy_mean: 0.70,
      accuracy_std: 0.09
    },
    user_id: "user456",
    created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    last_updated: new Date().toISOString(),
    event_name: "UFC 305",
    event_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    portfolio_returns: 0.12,
    previous_year_returns: 320.00,
    playbook_type: "simple",
    picks: []
  },
  // Special/Featured playbook
  {
    id: "special_featured",
    name: "Featured Championship Strategy",
    creator_type: "special",
    creator_name: "Odikai",
    is_public: true,
    avg_accuracy: 0.75,
    last_event_return: 0.20,
    accuracy_pct_last_event: 0.75,
    returns_by_event: generateReturnsArray(35, 0.12, 0.12),
    accuracy_by_event: generateAccuracyArray(35, 0.75, 0.07),
    longest_loss_streak: 1,
    longest_win_streak: 6,
    current_win_streak: 4,
    distributions_summary: {
      returns_mean: 0.12,
      returns_std: 0.12,
      accuracy_mean: 0.75,
      accuracy_std: 0.07
    },
    user_id: "system",
    created_at: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
    last_updated: new Date().toISOString(),
    event_name: "UFC 305",
    event_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    portfolio_returns: 0.12,
    previous_year_returns: 2100.00,
    playbook_type: "flex",
    picks: [
      {
        pick_name: "Leon Edwards",
        opponent_name: "Belal Muhammad",
        budget_amount: 0.18,
        american_odds: "+110",
        decimal_odds: 2.10,
        supporting_strategies: 10,
        type: "moneyline"
      }
    ]
  },
  // More user playbooks for variety
  {
    id: "user_playbook_3",
    name: "Balanced Approach",
    creator_type: "user",
    creator_name: "Mike Johnson",
    is_public: true,
    avg_accuracy: 0.63,
    last_event_return: 0.05,
    accuracy_pct_last_event: 0.63,
    returns_by_event: generateReturnsArray(12, 0.05, 0.08),
    accuracy_by_event: generateAccuracyArray(12, 0.63, 0.12),
    longest_loss_streak: 3,
    longest_win_streak: 2,
    current_win_streak: 0,
    distributions_summary: {
      returns_mean: 0.05,
      returns_std: 0.08,
      accuracy_mean: 0.63,
      accuracy_std: 0.12
    },
    user_id: "user789",
    created_at: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
    last_updated: new Date().toISOString(),
    event_name: "UFC 305",
    event_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    portfolio_returns: 0.05,
    previous_year_returns: 280.50,
    playbook_type: "simple",
    picks: []
  }
];

// Utility function to get all public playbooks (for Top Playbooks page)
export function getPublicPlaybooks() {
  return mockPlaybooksV2.filter(pb => pb.is_public === true);
}

// Utility function to get playbook by ID
export function getPlaybookById(id) {
  return mockPlaybooksV2.find(pb => pb.id === id);
}

// Utility function to inject dummy data into Firestore (for testing)
// Note: This would be used in development/testing, not production
export async function injectMockDataToFirestore(db) {
  const { collection, doc, setDoc } = await import('firebase/firestore');
  
  const playbooksCollection = collection(db, 'playbooks');
  
  for (const playbook of mockPlaybooksV2) {
    // All playbooks go into unified playbooks collection
    // Set is_prebuilt based on creator_type
    const isPrebuilt = playbook.creator_type === 'odikai' || playbook.creator_type === 'special';
    
    const playbookData = {
      ...playbook,
      is_prebuilt: isPrebuilt,
      // Ensure user_id is set for user-created playbooks
      ...(isPrebuilt ? {} : { user_id: playbook.user_id || 'mock_user_id' })
    };
    
    await setDoc(doc(playbooksCollection, playbook.id), playbookData);
  }
  
  if (process.env.NODE_ENV === 'development') {
    console.log(`Injected ${mockPlaybooksV2.length} mock playbooks into unified playbooks collection`);
  }
}

